import moment from 'moment';
import React from 'react';
import { FaCircleInfo } from "react-icons/fa6";
import { FiCalendar } from 'react-icons/fi';
import Tooltip from '../../../Components/Tooltip';
import lgbtqIcon from '../../../assets/images/lgbtq.png';
import { Agency, disabilityAccessible, gendersServed, locationType, roomType, urbanRuralClassification } from '../../../type/Agency';
import { formatUrl, isValidURL } from '../../../utils/helper';


const CollaborativeInfo = ({
    id,
    data: agency,
    groupId,
    onViewMap,
}: {
    id: string;
    data: Agency;
    groupId: string | null;
    onViewMap?: (agency: Agency) => void;
}) => {
    const { regionalSupporter, canAnswerRequest, canSendRequest } =
        agency.groupInfo?.find((groupInfo: any) => groupInfo?.groupId === groupId) || {};
    const isReferral = !canAnswerRequest && canSendRequest;
    const city = ((agency.details?.city || "").split(",")[0] || "").trim();
    const state = (
        agency.details?.state ||
        (agency.details?.city || "").split(",")[1] ||
        ""
    ).trim();

    return (
        <div id={id} className="card p-4 lg:p-5 2xl:p-8 mb-5">
            <div className="card-header flex sm:items-center sm:justify-between flex-col sm:flex-row mb-5 text-gray-600 font-medium">
                <div className="flex flex-wrap gap-3">
                    {isReferral && (
                        <span className="py-1 px-3 rounded-full text-secondary 2xl:text-sm bg-blue-100">Referral Only</span>
                    )}
                    {canAnswerRequest && (
                        <span className="py-1 px-3 rounded-full text-white 2xl:text-sm bg-primary">Shelter</span>
                    )}
                    {regionalSupporter && (
                        <span className="py-1 px-3 rounded-full text-white 2xl:text-sm bg-secondary">Regional Supporter</span>
                    )}
                </div>

                {agency.profile.memberSince != "__private__" && (
                    <div className="flex items-center mt-3 sm:mt-0">
                        <FiCalendar className="me-3" size={20} />
                        <span className="text-sm 2xl:text-base">Member since{" "}
                            {moment(agency.profile.memberSince || agency.createdAt).format(
                                "MMMM YYYY",
                            )}
                        </span>
                    </div>
                )}
            </div>
            <h5 className='text-secondary font-semibold text-base lg:text-lg mb-3 lg:mb-5'>{agency.name}</h5>
            <div className="flex flex-col md:flex-row text-sm 2xl:text-base">
                <div className="md:w-1/3 mb-3 md:mb-0">
                    <p className="text-dark font-medium mb-2 md:mb-3">Location info:</p>
                    <p className="text-gray-600 font-semibold">
                        {city}
                        {state ? ", " + state : ""}
                    </p>
                    {agency?.profile?.county != "__private__" && (
                        <p>{agency?.profile?.county}</p>
                    )}
                    {typeof onViewMap == "function" && (
                        <a className="text-secondary font-semibold cursor-pointer" onClick={() => onViewMap(agency)}>View on map</a>
                    )}
                </div>
                {canAnswerRequest && (
                    <div className="md:w-1/3 mb-3 md:mb-0">
                        <p className="text-dark font-medium mb-2 md:mb-3">About the shelter:</p>
                        <Referral agency={agency} />
                        <GendersServed agency={agency} />
                        <RoomType agency={agency} />
                        <AdaCompliant agency={agency} />
                        <DisabilityAccessible agency={agency} />
                        <PublicTransportation agency={agency} />
                        <UrbanRuralClassification agency={agency} />
                        <LocationType agency={agency} />
                        <LGBTQFriendly agency={agency} isModal={true} />
                        <ShelterInformationForthcoming agency={agency} />
                    </div>
                )}
                {agency?.profile?.website && isValidURL(agency.profile.website) && (
                    <div className="md:w-1/3 mb-3 md:mb-0">
                        <p className="text-dark font-medium mb-2 md:mb-3">Website</p>
                        {agency.profile.website != "__private__" && (
                            <a href={formatUrl(agency.profile.website)} target="_blank" className='text-primary font-semibold break-words'>
                                {agency.profile.website}
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default CollaborativeInfo;

interface Props {
    agency: Agency;
    isModal?: boolean;
}

export const Referral: React.FC<Props> = ({ agency }) => {
    return agency.profile.referral ? <p>Referral agency only</p> : null;
};

export const GendersServed: React.FC<Props> = ({ agency }) => {
    return agency.profile.gendersServed &&
        agency.profile.gendersServed !== "__private__" ?
        <p>{gendersServed[agency.profile.gendersServed]}</p> : null
}

export const RoomType: React.FC<Props> = ({ agency }) => {
    return agency.profile.roomType &&
        agency.profile.roomType !== "__private__" ?
        <p>{roomType[agency.profile.roomType]}</p> : null
}

export const AdaCompliant: React.FC<Props> = ({ agency }) => {
    return agency.profile.adaCompliant &&
        agency.profile.adaCompliant !== "__private__" &&
        !agency.profile.disabilityAccessible ?
        <p>{!agency.profile.adaCompliant ? "Not " : ""}ADA compliant</p> : null
}

export const DisabilityAccessible: React.FC<Props> = ({ agency }) => {
    return agency.profile.disabilityAccessible &&
        agency.profile.disabilityAccessible !== "__private__" ?
        <p>{disabilityAccessible[`${agency.profile.disabilityAccessible}`]}</p> : null
}

export const PublicTransportation: React.FC<Props> = ({ agency }) => {
    return agency.profile.publicTransportation &&
        agency.profile.publicTransportation !== "__private__" ?
        <p>
            {agency.profile.publicTransportation ? "Near" : "No nearby"} public
            transportation
        </p> : null
}

export const UrbanRuralClassification: React.FC<Props> = ({ agency }) => {
    return agency.profile.urbanRuralClassification &&
        agency.profile.urbanRuralClassification !== "__private__" ?
        <p>{urbanRuralClassification[agency.profile.urbanRuralClassification]}</p> : null
}

export const LocationType: React.FC<Props> = ({ agency }) => {
    return agency.profile.locationType &&
        agency.profile.locationType !== "__private__" ?
        <p>{locationType[agency.profile.locationType]}</p> : null
}

export const LGBTQFriendly: React.FC<Props> = ({ agency, isModal }) => {

    const tooltipText = `Shelters responded Yes to at least one of the following questions:
            <br />* Does your organization provide tailored services and programs
            to people who identify as LGBTQ?
            <br />* Does your agency in general identify as an LGBTQ ally?`;

    return agency.profile.lgbtqFriendly !== "__private__" &&
        agency.profile.lgbtqFriendly ? (

        <p>
            <span id="lgbtq" className=""></span>
            <span className="flex items-center gap-1"><img src={lgbtqIcon} alt="lgbtqIcon" width={'25px'} /> LGBTQ friendly&nbsp;  <Tooltip text={tooltipText}><span className="cursor-pointer"><FaCircleInfo size="16" className="text-gray-600" /></span></Tooltip></span>
            <a className="popover-link" role="button">
                <span
                    data-tooltip-id={
                        isModal ? "modal-tooltip-" : "normal-tooltip-" + agency.agencyId
                    }
                    className="glyphicon glyphicon-info-sign tooltip-icon info"
                />
            </a>
        </p>
    ) : null
}

export const ShelterInformationForthcoming: React.FC<Props> = ({ agency }) => {
    return !(
        (agency.profile.gendersServed &&
            agency.profile.gendersServed !== "__private__") ||
        (agency.profile.roomType && agency.profile.roomType !== "__private__") ||
        (agency.profile.adaCompliant &&
            agency.profile.adaCompliant !== "__private__") ||
        (agency.profile.disabilityAccessible &&
            agency.profile.disabilityAccessible !== "__private__") ||
        (agency.profile.publicTransportation &&
            agency.profile.publicTransportation !== "__private__") ||
        (agency.profile.urbanRuralClassification &&
            agency.profile.urbanRuralClassification !== "__private__") ||
        (agency.profile.locationType &&
            agency.profile.locationType !== "__private__") ||
        (agency.profile.lgbtqFriendly &&
            agency.profile.lgbtqFriendly !== "__private__") ||
        agency.profile.referral
    ) ? <p>Shelter information forthcoming.</p> : null;
}